<script lang="ts">
import Calendar from 'lucide-svelte/icons/calendar';
import MapPin from 'lucide-svelte/icons/map-pin';
import Timer from 'lucide-svelte/icons/timer';

interface Props {
    title?: string;
    date?: string;
    location?: string;
    duration?: string;
}

let { title = '', date = '', location = '', duration = '' }: Props = $props();

// Get ordinal suffix for day
function getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
}

// Format date for display
function formatDate(dateString: string): string {
    const date = new Date(dateString);

    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();

    // Format time as 12-hour with lowercase am/pm
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

    return `${weekday}, ${day}${getOrdinalSuffix(day)} ${month} ${year} at ${hours}:${minutesStr}${ampm}`;
}

let dateUpdated = $derived(date ? new Date(date) : null);
</script>

<div class="event-header">
    <div class="event-header__wrapper">
        <h1 class="event-title"><span>{title}</span></h1>
        {#if date}
            <div class="date">
                <Calendar size={18} />
                <time datetime={dateUpdated?.toISOString()}>
                    {formatDate(date)}
                </time>
            </div>
        {/if}
        {#if duration && duration.trim()}
            <div class="duration">
                <Timer size={18} />
                {duration}
            </div>
        {/if}
        {#if location && location.trim()}
            <div class="location">
                <MapPin size={18} />
                {location}
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use 'sass-mq/mq' as *;

    .event-header {
        display: grid;
        place-items: center;
        &__wrapper {
            border-bottom: 1px solid var(--colour-noghrei-silver);
            margin: 0 var(--layout-pagegrid-margin) 2.5rem;
            max-width: 76rem;
            padding-bottom: 4.4rem;
            @include mq($from: tablet) {
                margin-bottom: 5rem;
            }
        }
    }

    .event-title {
        @include typography.display-article;
        padding-bottom: 1.6rem;
        text-align: center;
        text-wrap: balance;
        @include mq($from: tablet) {
            padding-bottom: 2.8rem;
        }

        span {
            display: block;
            margin: 0 auto;
            max-width: 40ch;
        }
    }

    .date,
    .duration,
    .location {
        @include typography.body-lg;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.8rem;

        &:not(:last-child) {
            padding-bottom: 1.6rem;
            @include mq($from: tablet) {
                padding-bottom: 2.8rem;
            }
        }

        :global(svg) {
            flex-shrink: 0;
        }
    }

    .date {
        color: var(--colour-squant);
    }

    .duration {
        color: var(--colour-squant);
    }

    .location {
        color: var(--colour-squant);
    }
</style>
